import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(advancedFormat);

export function isDefaultDate(date: string) {
  // Default date is of the format "0001-01-01 00:00:00.0000 +0000 UTC"
  const defaultDateRegExp = /^0001-01-01 00:00:00\.?\d* ?[+|-]?\d* ?.*$/;

  // Default ISO date is of the format "0001-01-01T00:00:00Z"
  const defaultISODateRegExp = /^0001-01-01T00:00:00Z$/;

  return defaultDateRegExp.test(date) || defaultISODateRegExp.test(date);
}

export function isValidUTCDate(date: string) {
  // UTC date is of the format "2022-03-02 12:45:34.327345 +0000 UTC"
  const utcDateRegExp = /^\d\d\d\d-\d\d-\d\d \d\d:\d\d:\d\d\.?\d* ?[+|-]?\d* ?.*$/;
  return utcDateRegExp.test(date);
}

export function isValidISODate(date: string) {
  // ISO date is of the format "2022-03-02T12:45:34.327345Z"
  const isoDateRegExp = /^\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d\.?\d*Z$/;
  return isoDateRegExp.test(date);
}

interface GetPastUnixTime {
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}

/**
 * Returns a UNIX timestamp (in milliseconds) for a time in the past,
 * calculated from the start of today, subtracting the specified duration.
 */
export function getPastUnixTime(args: GetPastUnixTime): number {
  let target = dayjs().startOf('day');
  if (args.weeks) target = target.subtract(args.weeks, 'week');
  if (args.days) target = target.subtract(args.days, 'day');
  if (args.hours) target = target.subtract(args.hours, 'hour');
  if (args.minutes) target = target.subtract(args.minutes, 'minute');
  if (args.seconds) target = target.subtract(args.seconds, 'second');

  // Return the resulting timestamp in milliseconds
  return target.valueOf();
}

interface ConvertTOISOResults {
  isValid: boolean;
  convertedDate: string;
}

/**
 * Function to convert
 */
export function convertToISO(date: string): ConvertTOISOResults {
  /**
   * Convert date from '2022-03-02 12:45:34.327345 +0000 UTC' format
   * to '2022-03-02T12:45:34.327345Z'.
   */
  if (isValidUTCDate(date)) {
    const splits = date.split(' ');
    return {
      isValid: true,
      convertedDate: `${splits[0]}T${splits[1]}Z`,
    };
  }

  if (isValidISODate(date)) {
    return {
      isValid: true,
      convertedDate: date,
    };
  }

  return {
    isValid: false,
    convertedDate: date,
  };
}

/**
 * Returns the latest date from the provided date strings in ISO format.
 */
export function getLatestOfDateStringsInISOFormat(...dates: (string | undefined)[]): string | undefined {
  if (dates.length === 0) {
    return undefined;
  }

  const now = dayjs();
  let closestISODate: string | undefined = undefined;
  let smallestDiff = Infinity;

  for (const date of dates) {
    if (!date) {
      continue;
    }

    const conversionResults = convertToISO(date);
    if (!conversionResults.isValid) {
      continue;
    }

    const isoDate = conversionResults.convertedDate;
    const dateObj = dayjs(isoDate);
    const diff = now.diff(dateObj); // difference in milliseconds
    if (diff >= 0 && diff < smallestDiff) {
      smallestDiff = diff;
      closestISODate = isoDate;
    }
  }

  return closestISODate;
}

/**
 * Returns a formatted date string for the provided date of birth.
 * The format is 'MMM do, yyyy' (e.g., 'Jan 1st, 2000').
 */
export function getFormattedDateOfBirth(dateOfBirth: string): string {
  if (!dateOfBirth || isDefaultDate(dateOfBirth)) {
    return '';
  }

  const date = dayjs(dateOfBirth);
  if (!date.isValid()) {
    return '';
  }

  return date.format('MMM Do, YYYY');
}

export type AgeSuffix = 'year' | 'years' | 'month' | 'months' | 'day' | 'days';

interface AgeDetails {
  age: number;
  suffix: AgeSuffix;
}

/**
 * Returns the age and its suffix based on the provided date of birth.
 * If the date is invalid or in the future, returns null.
 */
export function getAgeFromDateOfBirth(dateOfBirth: string): AgeDetails | null {
  if (!dateOfBirth || isDefaultDate(dateOfBirth)) {
    return null;
  }

  const date = dayjs(dateOfBirth);
  if (!date.isValid()) {
    return null;
  }

  const today = dayjs();
  let age = today.diff(date, 'year');
  let suffix: AgeSuffix = 'year';

  if (age < 0) {
    return null; // Future date
  } else if (age === 0) {
    age = today.diff(date, 'month');
    suffix = age > 1 ? 'months' : 'month';
    if (age === 0) {
      age = today.diff(date, 'day');
      suffix = age > 1 ? 'days' : 'day';
    }
  } else if (age > 1) {
    suffix = 'years';
  }

  return { age, suffix };
}
